import React, { useEffect, useState } from "react";
import { Form, Button, Image, Icon, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { ENV } from "../../../../Utils/constants";
import { Editor } from "@tinymce/tinymce-react";
import {
  initialValuesOne,
  validationSchemaOne,
  categoryOptions,
  subcategoryOptions,
} from "./StepOne.form";
import { SceneFormFile } from "./SceneFormFile";
import { VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./SceneForm.scss";

const virtualSceneController = new VirtualScenes();

export function StepOne({
  formData,
  updateFormData,
  prevStep,
  isUpdateMode,
  scene,
  onReload,
  onClose,
}) {
  const { accessToken } = useAuth();
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [fileFieldName, setFileFieldName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [moduleData, setModuleData] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({
    bannertop: formData.bannertop || null,
  });

  // Carga datos previos en edición
  useEffect(() => {
    if (!isUpdateMode || !scene?.refId) return;
    (async () => {
      try {
        const data = await virtualSceneController.getVirtualScenesModeule(
          scene.refId,
          accessToken,
          "module_01"
        );
        setModuleData(data.moduleData);
      } catch (err) {
        console.error("Error fetching module data:", err);
      }
    })();
  }, [isUpdateMode, accessToken, scene?.refId]);

  const formik = useFormik({
    initialValues: isUpdateMode
      ? initialValuesOne({ ...moduleData, refId: scene.refId })
      : initialValuesOne(formData),
    validationSchema: validationSchemaOne(),
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        const sceneId = isUpdateMode
          ? scene.refId
          : values.idScene;

        // Agregar tag pendiente
        if (values.tagsInput) {
          values.ModOneTags = [
            ...values.ModOneTags,
            values.tagsInput.trim(),
          ];
          values.tagsInput = "";
        }

        await virtualSceneController.createModuleAdd(
          "module_01",
          accessToken,
          sceneId,
          values
        );
      } catch (err) {
        console.error("Error al enviar el formulario:", err);
        return;
      }
      updateFormData({ ...values, ...uploadedFiles });
      onReload();
      onClose();
    },
  });

  // Manejo de subida de archivo
  const handleFileSubmit = (fileData) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [fileFieldName]: fileData,
    }));
    formik.setFieldValue(fileFieldName, fileData.id);
    setIsFileModalOpen(false);
  };

  // Eliminación de archivo
  const handleRemoveFile = async () => {
    const fileId = uploadedFiles.bannertop?._id;
    if (!fileId) return;
    try {
      await virtualSceneController.deleteImageRepo(accessToken, fileId);
      setUploadedFiles({ bannertop: null });
      formik.setFieldValue("bannertop", null);
    } catch (err) {
      console.error("Error al eliminar archivo:", err);
    }
  };

  return (
    <Form className="virtual-scene-form" onSubmit={formik.handleSubmit}>
      {/* Título */}
      <Form.Input
        label="Título de la escena virtual"
        name="ModOneTitle"
        placeholder="Ingrese el título"
        onChange={formik.handleChange}
        value={formik.values.ModOneTitle}
        error={formik.errors.ModOneTitle}
      />

      {/* Categoría */}
      <Form.Select
        label="Categoría"
        name="ModOneCategory"
        options={categoryOptions}
        placeholder="Seleccione la categoría"
        multiple
        value={formik.values.ModOneCategory}
        onChange={(_, { name, value }) =>
          formik.setFieldValue(name, value)
        }
        error={formik.errors.ModOneCategory}
      />

      {/* Subcategoría */}
      <Form.Select
        label="Subcategoría"
        name="ModOneSubcategory"
        options={subcategoryOptions}
        placeholder="Seleccione la(s) subcategoría(s)"
        multiple
        value={formik.values.ModOneSubcategory}
        onChange={(_, { name, value }) =>
          formik.setFieldValue(name, value)
        }
        error={formik.errors.ModOneSubcategory}
      />

      {/* Etiquetas existentes */}
      <div className="tags-container">
        {formik.values.ModOneTags.map((tag, i) => (
          <Label key={i}>{tag}</Label>
        ))}
      </div>

      {/* Input para nueva etiqueta */}
      <Form.Input
        label="Etiquetas"
        name="tagsInput"
        placeholder="Agregar etiqueta"
        value={formik.values.tagsInput}
        onChange={formik.handleChange}
        error={formik.errors.ModOneTags}
      />
      <Button
        type="button"
        onClick={() => {
          if (formik.values.tagsInput) {
            formik.setFieldValue("ModOneTags", [
              ...formik.values.ModOneTags,
              formik.values.tagsInput.trim(),
            ]);
            formik.setFieldValue("tagsInput", "");
          }
        }}
      >
        Añadir Etiqueta
      </Button>

      {/* Descripción */}
      <label className="field-label">Descripción</label>
      <Editor
        apiKey={ENV.TINY_APIKEY}
        init={{
          height: 200,
          menubar: true,
          plugins: ["link", "lists", "autolink", "image"],
          toolbar:
            "undo redo | bold italic underline | bullist numlist outdent indent",
        }}
        value={formik.values.ModOneDescription}
        onEditorChange={(content) =>
          formik.setFieldValue("ModOneDescription", content)
        }
      />

      {/* URL de la sala */}
      <Form.Input
        label="URL de la sala"
        name="ModOneUrlsala"
        placeholder="https://"
        onChange={formik.handleChange}
        value={formik.values.ModOneUrlsala}
        error={formik.errors.ModOneUrlsala}
      />

      {/* Upload de portada */}
      <div className="upload-field">
        <label>Imagen de Portada</label>
        <Button
          type="button"
          onClick={() => {
            setFileFieldName("bannertop");
            setIsPublic(true);
            setIsFileModalOpen(true);
          }}
        >
          Seleccionar imagen
        </Button>

        {uploadedFiles.bannertop && (
          <div className="image-preview">
            <Image
              src={uploadedFiles.bannertop.url}
              size="small"
              centered
            />
            <Icon
              name="close"
              onClick={handleRemoveFile}
              style={{ cursor: "pointer" }}
            />
          </div>
        )}

        {formik.errors.bannertop && (
          <div className="error-message">{formik.errors.bannertop}</div>
        )}
      </div>

      <SceneFormFile
        isOpen={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        onSubmit={handleFileSubmit}
        isPublic={isPublic}
        idScene={formik.values.idScene}
        accept="image/*"
        buttonText="Seleccionar imagen"
      />

      {/* Botones */}
      <Form.Group className="formgroup">
        <Button type="button" color="orange" onClick={prevStep}>
          Anterior
        </Button>
        <Button type="submit" primary loading={formik.isSubmitting}>
          Subir
        </Button>
      </Form.Group>
    </Form>
  );
}
